import { useMemo } from 'react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { FaBirthdayCake, FaUserPlus } from 'react-icons/fa';
import { Employee } from '../../../hooks/useEmployeeData';
import EmployeeAvatar from '../../../components/EmployeeAvatar';
import { colors } from '../styles/personalakteStyles';

interface EmployeeInfoBannerProps {
  activeEmployees: Employee[];
}

function EmployeeInfoBanner({ activeEmployees }: EmployeeInfoBannerProps) {
  const upcomingBirthdays = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const lookAheadDays = 14;
    return activeEmployees
      .filter(emp => emp.birth_date)
      .map(emp => {
        const [y, m, d] = emp.birth_date.split('-').map(Number);
        const birthdayThisYear = new Date(today.getFullYear(), m - 1, d);
        let nextBirthday = birthdayThisYear;
        if (birthdayThisYear < today) {
          nextBirthday = new Date(today.getFullYear() + 1, m - 1, d);
        }
        const diffMs = nextBirthday.getTime() - today.getTime();
        const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
        const age = nextBirthday.getFullYear() - y;
        return { emp, diffDays, age, date: nextBirthday };
      })
      .filter(b => b.diffDays >= 0 && b.diffDays <= lookAheadDays)
      .sort((a, b) => a.diffDays - b.diffDays)
      .slice(0, 3);
  }, [activeEmployees]);

  const newEmployees = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return activeEmployees
      .filter(emp => {
        if (!emp.start_date) return false;
        const start = new Date(emp.start_date);
        return start >= thirtyDaysAgo && start <= today;
      })
      .sort((a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime())
      .slice(0, 3);
  }, [activeEmployees]);

  if (upcomingBirthdays.length === 0 && newEmployees.length === 0) return null;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 16,
      padding: '8px 14px',
      marginBottom: 12,
      background: 'rgba(255, 255, 255, 0.9)',
      border: `1px solid ${colors.border}`,
      borderRadius: 12,
      overflowX: 'auto',
    }}>
      {upcomingBirthdays.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FaBirthdayCake size={12} style={{ color: colors.magenta, flexShrink: 0 }} />
          <span style={{ fontSize: 10, fontWeight: 700, color: colors.slate, whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
            Birthdays
          </span>
          {upcomingBirthdays.map(({ emp, diffDays, age }) => {
            const dayLabel = diffDays === 0 ? 'Heute!' : diffDays === 1 ? 'Morgen' : `in ${diffDays}d`;
            const isToday = diffDays === 0;
            return (
              <Link
                key={emp.id}
                to={`/employees/${emp.id}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '3px 8px',
                  borderRadius: 9999,
                  background: isToday ? 'rgba(231, 0, 125, 0.08)' : colors.bgMuted,
                  border: `1px solid ${isToday ? 'rgba(231, 0, 125, 0.2)' : colors.borderLight}`,
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = isToday ? 'rgba(231, 0, 125, 0.14)' : colors.bgHover; }}
                onMouseLeave={e => { e.currentTarget.style.background = isToday ? 'rgba(231, 0, 125, 0.08)' : colors.bgMuted; }}
              >
                <EmployeeAvatar firstName={emp.first_name} lastName={emp.last_name} photoUrl={emp.photo_url} size={18} />
                <span style={{ fontSize: 11, fontWeight: 600, color: colors.textPrimary }}>{emp.first_name}</span>
                <span style={{ fontSize: 10, color: isToday ? colors.magenta : colors.textMuted, fontWeight: isToday ? 700 : 500 }}>{dayLabel}</span>
                <span style={{ fontSize: 9, color: colors.textMuted }}>{age}J</span>
              </Link>
            );
          })}
        </div>
      )}

      {upcomingBirthdays.length > 0 && newEmployees.length > 0 && (
        <div style={{ width: 1, height: 20, background: colors.border, flexShrink: 0 }} />
      )}

      {newEmployees.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FaUserPlus size={12} style={{ color: '#15803d', flexShrink: 0 }} />
          <span style={{ fontSize: 10, fontWeight: 700, color: colors.slate, whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
            New
          </span>
          {newEmployees.map(emp => {
            const startD = new Date(emp.start_date);
            const daysSince = Math.round((Date.now() - startD.getTime()) / (1000 * 60 * 60 * 24));
            const label = daysSince === 0 ? 'Heute' : daysSince === 1 ? 'Gestern' : `vor ${daysSince}d`;
            return (
              <Link
                key={emp.id}
                to={`/employees/${emp.id}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '3px 8px',
                  borderRadius: 9999,
                  background: 'rgba(21, 128, 61, 0.06)',
                  border: '1px solid rgba(21, 128, 61, 0.15)',
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(21, 128, 61, 0.12)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(21, 128, 61, 0.06)'; }}
              >
                <EmployeeAvatar firstName={emp.first_name} lastName={emp.last_name} photoUrl={emp.photo_url} size={18} />
                <span style={{ fontSize: 11, fontWeight: 600, color: colors.textPrimary }}>{emp.first_name}</span>
                <span style={{ fontSize: 10, color: colors.textMuted }}>{label}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default EmployeeInfoBanner;
